import { Combobox, Input, Label, MultiSelect, Textarea } from "@tellescope/ui";
import { Button, Select } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { ContentComboboxOption } from "./ContentComboboxOption";
import { CreateTagOption } from "./CreateTagOption";
import { EXISTING_MODE, UPLOAD_MODE, useShareContentForm } from "./useShareContentForm";

export interface ShareContentFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const ShareContentForm = ({ contactId, onSubmit, onCancel }: ShareContentFormProps) => {
  const { t } = useTranslation("contacts");
  const {
    form,
    setContentType,
    setAssignmentType,
    selectedContentOption,
    contentOptions,
    isContentLoading,
    tagOptions,
    setTagOptions,
    selectedFile,
    handleFileChange,
    fileInputProps,
    isSubmitDisabled,
    submitLabel,
  } = useShareContentForm({ contactId, onSubmit });

  return (
    <form className="flex-1 overflow-y-auto space-y-6 pr-2" {...form.getFormProps()}>
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("Share Method")}</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={form.values.shareMode === EXISTING_MODE ? "solid" : "outline"}
            onClick={() => form.setFieldValue("shareMode", EXISTING_MODE)}
            className="flex-1"
          >
            {t("Select Existing Content")}
          </Button>
          <Button
            type="button"
            variant={form.values.shareMode === UPLOAD_MODE ? "solid" : "outline"}
            onClick={() => form.setFieldValue("shareMode", UPLOAD_MODE)}
            className="flex-1"
          >
            {t("Upload New Content")}
          </Button>
        </div>
      </div>

      {form.values.shareMode === EXISTING_MODE && (
        <div className="space-y-2">
          <Label htmlFor="content-select" className="text-sm font-medium">
            {t("Select Content")}
          </Label>
          <Combobox
            id="content-select"
            value={selectedContentOption}
            onChange={(option) => form.setFieldValue("contentId", option?.value ?? "")}
            options={contentOptions}
            placeholder={t("Choose content to share")}
            searchPlaceholder={t("Search content")}
            emptyText={t("No available content")}
            isLoading={isContentLoading}
            loadingText={t("Loading content...")}
            clearable
            className="w-full"
            // The default renderer truncates; wrap instead so long titles stay fully readable
            // within the popover's trigger-matched width.
            formatOption={({ option, isSelected }) => <ContentComboboxOption option={option} isSelected={isSelected} />}
          />
        </div>
      )}

      {form.values.shareMode === UPLOAD_MODE && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium">
                {t("Title")} <span className="text-destructive">*</span>
              </Label>
              <Input id="title" {...form.getFieldProps("title")} placeholder={t("Enter title")} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">
                {t("Description")}
              </Label>
              <Textarea
                id="description"
                {...form.getFieldProps("description")}
                placeholder={t("Enter description")}
                rows={3}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="content-type" className="text-sm font-medium">
                {t("Type")}
              </Label>
              <Select value={form.values.contentType} onValueChange={setContentType}>
                <Select.Trigger className="w-full">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="Article" disabled>
                    {t("Article")} ({t("Coming Soon")})
                  </Select.Item>
                  <Select.Item value="PDF">{t("PDF")}</Select.Item>
                  <Select.Item value="Video">{t("Video")}</Select.Item>
                </Select.Content>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">
                {t("Category")}
              </Label>
              <Input id="category" {...form.getFieldProps("category")} placeholder={t("Enter category")} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags" className="text-sm font-medium">
              {t("Tags")}
            </Label>
            <MultiSelect
              id="tags"
              options={tagOptions}
              {...form.getCustomFieldProps("tags")}
              placeholder={t("Add tags")}
              creator={{
                render: (inputValue) => <CreateTagOption inputValue={inputValue} />,
                transform: (inputValue) => inputValue,
                onCreate: (newOption) => {
                  setTagOptions([...tagOptions, newOption]);
                },
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignment-type" className="text-sm font-medium">
                {t("Assignment")}
              </Label>
              <Select value={form.values.assignmentType} onValueChange={setAssignmentType}>
                <Select.Trigger className="w-full">
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="All">{t("All")}</Select.Item>
                  <Select.Item value="By Tags">{t("By Tags")}</Select.Item>
                  <Select.Item value="Manual">{t("Manual")}</Select.Item>
                  <Select.Item value="Individual">{t("Individual")}</Select.Item>
                </Select.Content>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="portal-pin-index" className="text-sm font-medium">
                {t("Portal Pin Index")}
              </Label>
              <Input
                id="portal-pin-index"
                type="number"
                min={0}
                {...form.getFieldProps("portalPinIndex")}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">{t("Pins to top, 0 is highest")}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="content-upload" className="text-sm font-medium">
              {fileInputProps.label} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="content-upload"
              type="file"
              onChange={handleFileChange}
              accept={fileInputProps.accept}
              disabled={form.values.contentType === "Article"}
            />
            {selectedFile && (
              <p className="text-sm text-muted-foreground">
                {t("Selected: {{name}} ({{size}} MB)", {
                  name: selectedFile.name,
                  size: (selectedFile.size / 1024 / 1024).toFixed(2),
                })}
              </p>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitDisabled}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
